import { CLIENT_DOCUMENT_ISSUED_BY } from '../../../../../constants';

export const FLOW_PRINT = 'print';
export const FLOW_ECP = 'ecp';
export const flowKey = (dealId) => `tripartite-flow-${dealId}`;

export const PERSON_FIELDS = [
  { key: 'surname', label: 'Фамилия' },
  { key: 'name', label: 'Имя' },
  { key: 'middlename', label: 'Отчество' },
  { key: 'iin', label: 'ИИН' },
  { key: 'phone', label: 'Телефон', kind: 'phone', placeholder: '+7 (___) ___-__-__' },
  { key: 'documentNumber', label: 'Удостоверение №' },
  { key: 'documentDate', label: 'Дата выдачи', type: 'date' },
  { key: 'documentIssuer', label: 'Кем выдан', type: 'issuer' },
  { key: 'address', label: 'Адрес регистрации' },
];

export const CAR_FIELDS = [
  { key: 'issueYear', label: 'Год выпуска' },
  { key: 'vin', label: 'VIN / номер кузова' },
  { key: 'grnz', label: 'Госномер (ГРНЗ)' },
  { key: 'texNumber', label: 'СРТС №' },
  { key: 'texDate', label: 'СРТС от', type: 'date' },
  { key: 'color', label: 'Цвет' },
  { key: 'mileage', label: 'Пробег, км' },
];

export const REQUISITES_FIELDS = [
  { key: 'recipient', label: 'Получатель' },
  { key: 'iban', label: 'IBAN', kind: 'iban', placeholder: 'KZ000000000000000000' },
  { key: 'bankName', label: 'Банк' },
  { key: 'bic', label: 'БИК / BIC', kind: 'bic', placeholder: 'KCJBKZKX' },
];

const DATE_KEYS = ['documentDate', 'texDate'];

const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');
const digits = (value) => String(value || '').replace(/\D/g, '');

export const normalizePhone = (value) => {
  let next = digits(value);
  if (next.startsWith('8') && next.length === 11) next = `7${next.slice(1)}`;
  return next.slice(0, 11);
};
export const validPhone = (value) => {
  const next = digits(value);
  return next.length === 11 && next.startsWith('7');
};
export const displayPhone = (value) => {
  const next = normalizePhone(value);
  if (!validPhone(next)) return String(value || '').trim();
  return `+7 (${next.slice(1, 4)}) ${next.slice(4, 7)}-${next.slice(7, 9)}-${next.slice(9, 11)}`;
};

// Казахстан с 2024 на одном поясе UTC+5. Бэк кладёт smsAt/signedAt как UTC ISO.
export const KZ_TIME_ZONE = 'Asia/Almaty';
export const formatWhen = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    timeZone: KZ_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
};
export const cleanIban = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
export const validIban = (value) => /^KZ[0-9A-Z]{18}$/.test(cleanIban(value));
export const cleanBic = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
export const validBic = (value) => /^[A-Z]{8}([A-Z0-9]{3})?$/.test(cleanBic(value));
export const formatField = (kind, value) => {
  if (kind === 'phone') return normalizePhone(value);
  if (kind === 'iban') return cleanIban(value);
  if (kind === 'bic') return cleanBic(value);
  return value;
};

const emptyToNull = (obj) => {
  const next = { ...obj };
  DATE_KEYS.forEach((key) => {
    if (key in next && !next[key]) next[key] = null;
  });
  return next;
};

const personForm = (person) => ({
  surname: person?.surname || '',
  name: person?.name || '',
  middlename: person?.middlename || '',
  iin: person?.iin || '',
  phone: normalizePhone(person?.phone),
  documentNumber: person?.documentNumber || '',
  documentDate: toDateInput(person?.documentDate),
  documentIssuer: person?.documentIssuer || '',
  address: person?.address || '',
});

export const formFromValues = (values, parties) => {
  const seller = personForm(values?.seller);
  return {
    seller,
    buyer: personForm(values?.buyer),
    car: {
      issueYear: values?.car?.issueYear || '',
      vin: values?.car?.vin || '',
      grnz: values?.car?.grnz || '',
      texNumber: values?.car?.texNumber || '',
      texDate: toDateInput(values?.car?.texDate),
      color: values?.car?.color || '',
      mileage: values?.car?.mileage || '',
    },
    requisites: {
      recipient:
        values?.requisites?.recipient ||
        [seller.surname, seller.name, seller.middlename].filter(Boolean).join(' '),
      iban: cleanIban(values?.requisites?.iban),
      bankName: values?.requisites?.bankName || '',
      bic: cleanBic(values?.requisites?.bic),
    },
    agent: {
      director: values?.agent?.director || '',
      basis: values?.agent?.basis || 'Устава',
      phone: normalizePhone(values?.agent?.phone || parties?.agent?.phone),
    },
    terms: {
      agentFee: String(values?.agentFee || 1000),
      payoutDays: String(values?.payoutDays || 3),
      handoverDays: String(values?.handoverDays || 3),
      defects: values?.defects || '',
      keysCount: values?.keysCount ? String(values.keysCount) : '',
      extraEquipment: values?.extraEquipment || '',
    },
    price: values?.price || '',
  };
};

export const issuerOption = (raw) => {
  const byValue = CLIENT_DOCUMENT_ISSUED_BY.find((item) => String(item.value) === String(raw));
  if (byValue) return byValue;
  const text = String(raw || '').toLowerCase();
  if (text.includes('мю') || text.includes('юстиц')) return CLIENT_DOCUMENT_ISSUED_BY[1];
  if (text.includes('мвд') || text === '1') return CLIENT_DOCUMENT_ISSUED_BY[0];
  return null;
};

export const personName = (p) => [p?.surname, p?.name, p?.middlename].filter(Boolean).join(' ');

export const ecpSmsPhone = (form, role) =>
  normalizePhone(role === 'agent' ? form?.agent?.phone : form?.[role]?.phone);

export const fieldDomId = (path) => `tripartite-${String(path).replace(/\./g, '-')}`;

export const focusFirstError = (paths) => {
  const path = (paths || []).find(Boolean);
  if (!path || typeof document === 'undefined') return;
  const root = document.getElementById(fieldDomId(path));
  if (!root) return;
  const target =
    root.matches?.('input, textarea, select')
      ? root
      : root.querySelector('input, textarea, select, [tabindex]:not([tabindex="-1"])');
  const el = target || root;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.focus?.({ preventScroll: true });
};

export const formErrors = (form, missingFromServer, flow) => {
  const missing = (missingFromServer || []).filter((path) => {
    const [section, key] = path.split('.');
    if (!form) return true;
    if (!key) return section === 'price' ? !form.price : true;
    return !String(form?.[section]?.[key] || '').trim();
  });
  const invalid = [];
  if (form) {
    [
      ['seller.phone', form.seller?.phone, flow === FLOW_ECP],
      ['buyer.phone', form.buyer?.phone, flow === FLOW_ECP],
      ['agent.phone', form.agent?.phone, flow === FLOW_ECP],
    ].forEach(([path, value, required]) => {
      const filled = String(value || '').trim();
      if (!filled) {
        if (required) invalid.push(path);
        return;
      }
      if (!validPhone(value)) invalid.push(path);
    });
    if (form.requisites?.iban && !validIban(form.requisites.iban)) invalid.push('requisites.iban');
    if (form.requisites?.bic && !validBic(form.requisites.bic)) invalid.push('requisites.bic');
    // Вознаграждение удерживают из выплаты продавцу: пустое поле бэк молча
    // проигнорировал бы и напечатал в договор прежнюю сумму.
    if (!(Number(form.terms?.agentFee) > 0)) invalid.push('terms.agentFee');
  }
  const blocked = [...missing, ...invalid];
  return {
    missing,
    invalid,
    blocked,
    isError: (section, key) => blocked.includes(section) || blocked.includes(`${section}.${key}`),
  };
};

export const savePayload = (dealId, form, extra = {}) => ({
  dealId,
  seller: emptyToNull(form.seller),
  buyer: emptyToNull(form.buyer),
  car: emptyToNull(form.car),
  requisites: form.requisites,
  agent: form.agent,
  terms: {
    agentFee: Number(form.terms.agentFee) || 0,
    payoutDays: Number(form.terms.payoutDays) || 3,
    handoverDays: Number(form.terms.handoverDays) || 3,
    defects: form.terms.defects,
    keysCount: Number(form.terms.keysCount) || 0,
    extraEquipment: form.terms.extraEquipment,
  },
  price: Number(form?.price) || 0,
  confirmWipe: Boolean(extra.confirmWipe),
});

const ecpBusy = (ecp) => {
  const signed = Number(ecp?.signedCount) || 0;
  if (signed > 0 || ecp?.stage === 'complete' || ecp?.stage === 'waiting') return true;
  return (ecp?.signers || []).some((s) => s.status === 'sms' || s.status === 'signed');
};

export const regenerateWarning = (ecp, hasFile) => {
  if (!hasFile) return '';
  const signed = Number(ecp?.signedCount) || 0;
  if (ecp?.stage === 'complete' || signed >= 3) {
    return 'Договор подписан 3 сторонами. Новый документ аннулирует подписи и процесс начнётся заново.';
  }
  if (signed > 0) {
    return signed === 1
      ? 'Есть 1 подпись. Процесс подписания начнётся заново.'
      : `Есть ${signed} подписи. Процесс подписания начнётся заново.`;
  }
  if (ecpBusy(ecp)) {
    return 'СМС уже отправлена. Процесс подписания начнётся заново.';
  }
  return 'Документ будет заменён.';
};

export const restartEcpWarning = (ecp) => {
  const signed = Number(ecp?.signedCount) || 0;
  if (signed > 0 || ecp?.stage === 'complete') {
    return 'Подписи будут сброшены.';
  }
  return 'Подписание начнётся заново.';
};

export const switchFlowWarning = (ecp) => {
  if (!ecpBusy(ecp)) return '';
  const signed = Number(ecp?.signedCount) || 0;
  if (signed > 0) return 'Договор частично подписан ЭЦП.';
  return 'Идёт подписание ЭЦП.';
};

export const canGoNext = ({ flow, hasFile, ecp, nextStep }) => {
  if (!nextStep || !hasFile) return false;
  if (flow === FLOW_ECP) return ecp?.stage === 'complete';
  return true;
};
